import MiniListingCard from "@/components/MiniListingCard";
import ThumbListingCard from "@/components/ThumbListingCard";
import SoldThumb from "@/components/SoldThumb";
import AutoScrollStrip from "@/components/AutoScrollStrip";

/**
 * The "featured" band above the main listing grid on Browse. Renders:
 * - Top Dog (most-expensive card, sets the reference height)
 * - Just Listed marquee, Fan Favorites row, Recently Sold marquee
 * - The Pack — small community stats pillar with live "active now" pulse
 * Hidden entirely when the user has active filters (see Browse.jsx).
 */
export default function BrowseFeaturedStrip({ featured, likedIds, stats, recentlySold }) {
  return (
    <div className="mb-6" data-testid="browse-featured">
      <div className="flex flex-col md:flex-row gap-3 items-stretch">
        {/* Top Dog — anchor of the strip, sets the reference height */}
        {featured.most_expensive && (
          <section className="shrink-0 w-[140px]" data-testid="featured-most-expensive">
            <div className="flex items-baseline justify-between mb-1.5">
              <h2 className="font-mono text-[10px] text-[#FFD700] tracking-widest uppercase">→ Top Dog</h2>
            </div>
            <MiniListingCard listing={featured.most_expensive} liked={likedIds.has(featured.most_expensive.id)} />
          </section>
        )}

        {/* Just Listed + Fan Favorites + Recently Sold stacked to match Top Dog's height */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {featured.recent.length > 0 && (
            <section className="min-w-0" data-testid="featured-recent">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="font-mono text-[10px] text-white tracking-widest uppercase">→ Just Listed</h2>
                <span className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Freshest 15</span>
              </div>
              <AutoScrollStrip testId="featured-recent-strip" speedPxPerSec={35}>
                {featured.recent.map((l) => (
                  <div key={l.id} className="shrink-0 w-[90px]">
                    <ThumbListingCard listing={l} />
                  </div>
                ))}
              </AutoScrollStrip>
            </section>
          )}

          {featured.most_liked.length > 0 && (
            <section className="min-w-0" data-testid="featured-most-liked">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="font-mono text-[10px] text-[#FF5F1F] tracking-widest uppercase">→ Fan Favorites</h2>
                <span className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Top 8</span>
              </div>
              <div className="flex gap-1" data-testid="featured-most-liked-row">
                {featured.most_liked.map((l) => (
                  <div key={l.id} className="flex-1 min-w-0">
                    <ThumbListingCard listing={l} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {recentlySold.length > 0 && (
            <section className="min-w-0" data-testid="featured-recently-sold">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="font-mono text-[10px] text-emerald-400 tracking-widest uppercase">→ Recently Sold</h2>
                <span className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Anonymous</span>
              </div>
              <AutoScrollStrip testId="featured-recently-sold-strip" speedPxPerSec={30}>
                {recentlySold.map((item, i) => (
                  <div key={`${item.listing_id}-${i}`} className="shrink-0 w-[90px]">
                    <SoldThumb item={item} />
                  </div>
                ))}
              </AutoScrollStrip>
            </section>
          )}
        </div>

        {/* Community stats — small pillar with live active-now pulse */}
        {stats && (
          <section
            className="shrink-0 w-[140px] h57-surface p-3 flex flex-col justify-between"
            data-testid="featured-stats"
          >
            <div className="font-mono text-[10px] text-[#007AFF] tracking-widest uppercase">→ The Pack</div>
            <div data-testid="stat-active-now" className="pt-1">
              <div className="flex items-baseline gap-1.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 h57-live-pulse"
                  style={{ borderRadius: "9999px" }}
                  aria-hidden="true"
                />
                <div className="font-display font-black text-2xl tabular-nums leading-none text-emerald-400">
                  {Number(stats.active_now ?? 0).toLocaleString()}
                </div>
              </div>
              <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase mt-0.5">
                active now
              </div>
            </div>
            {[
              ["members", stats.users, "text-white"],
              ["vendors", stats.vendors, "text-[#FF5F1F]"],
              ["listings", stats.listings, "text-[#FFD700]"],
              ["cards sold", stats.cards_sold ?? 0, "text-emerald-400"],
            ].map(([label, value, color]) => (
              <div key={label} data-testid={`stat-${label.replace(" ", "-")}`}>
                <div className={`font-display font-black text-2xl tabular-nums leading-none ${color}`}>
                  {Number(value).toLocaleString()}
                </div>
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase mt-0.5">
                  {label}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
      <div className="mt-4 border-b border-white/10" />
    </div>
  );
}
